import React, { useEffect, useState } from 'react';
import api, { getApiErrorMessage } from '../api';
import './AdminModerationAI.css';
import './AdminNewsletter.css';

const SOURCE_LABELS = {
    db: 'задано в админке',
    env: 'из .env',
    default: 'по умолчанию',
};

const FIELDS = ['from_email', 'from_name', 'base_url', 'timeout_sec', 'news_mode', 'news_from_email', 'news_from_name'];
const NEWS_FIELDS = ['news_mode', 'news_from_email', 'news_from_name'];

const NEWS_MODES = [
    ['same', 'Как служебные письма', 'Тот же адрес и тот же основной ключ из раздела 1. Ничего дополнительно вводить не нужно.'],
    ['sender', 'Свой адрес, основной ключ', 'Введите только адрес для рассылок (например, news@svodx.ru). Ключ — основной из раздела 1. Рекомендуется.'],
    ['key', 'Свой адрес и свой ключ', 'Введите адрес и отдельный API-ключ (второй аккаунт SMTP.BZ). Рассылки полностью отделены от служебных писем.'],
];

function SourceBadge({ source }) {
    if (!source) return null;
    return <span className={`mai-source mai-source--${source}`}>{SOURCE_LABELS[source] || source}</span>;
}

function formFromConfig(config) {
    return {
        from_email: config.from_email || '',
        from_name: config.from_name || '',
        base_url: config.base_url || '',
        timeout_sec: String(config.timeout_sec ?? 30),
        news_mode: config.news?.mode || 'same',
        news_from_email: config.news?.mode !== 'same' ? config.news?.from_email || '' : '',
        news_from_name: config.news?.mode !== 'same' ? config.news?.from_name || '' : '',
    };
}

// Почтовый сервер (SMTP.BZ): значения из админки важнее .env, менять их можно без перезапуска сервера.
function AdminMailSettings() {
    const [config, setConfig] = useState(null);
    const [form, setForm] = useState(null);
    const [apiKey, setApiKey] = useState('');
    const [newsKey, setNewsKey] = useState('');
    const [testTarget, setTestTarget] = useState('main');
    const [loadError, setLoadError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [notice, setNotice] = useState(null);
    const [testTo, setTestTo] = useState('');
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);

    const applyConfig = (data) => {
        setConfig(data);
        setForm(formFromConfig(data));
        setApiKey('');
        setNewsKey('');
    };

    useEffect(() => {
        api.get('/admin/mail-settings')
            .then((res) => applyConfig(res.data))
            .catch((err) => setLoadError(getApiErrorMessage(err, 'Не удалось загрузить настройки почты')));
    }, []);

    if (loadError) {
        return (
            <section className="admin-main-card">
                <h2 className="admin-main-card__title">Почта</h2>
                <div className="mai-body"><div className="mai-alert mai-alert--error">{loadError}</div></div>
            </section>
        );
    }
    if (!config || !form) {
        return (
            <section className="admin-main-card">
                <h2 className="admin-main-card__title">Почта</h2>
                <div className="mai-body">Загрузка…</div>
            </section>
        );
    }

    const initial = formFromConfig(config);
    const changed = FIELDS.filter((name) => form[name] !== initial[name]);
    const isDirty = changed.length > 0 || apiKey.trim() !== '' || newsKey.trim() !== '';
    const newsMode = form.news_mode;
    const keySource = config.sources?.api_key;

    const setField = (name, value) => {
        setForm((prev) => ({ ...prev, [name]: value }));
        setNotice(null);
    };

    const handleSave = async () => {
        const payload = {};
        changed.forEach((name) => {
            payload[name] = name === 'timeout_sec' ? (Number(form.timeout_sec) || null) : form[name].trim();
        });
        if (apiKey.trim()) payload.api_key = apiKey.trim();
        if (newsMode === 'key' && newsKey.trim()) payload.news_api_key = newsKey.trim();
        if (NEWS_FIELDS.some((name) => changed.includes(name)) || payload.news_api_key) {
            payload.news_mode = newsMode;
            if (newsMode === 'same') {
                delete payload.news_from_email;
                delete payload.news_from_name;
            }
        }
        setSaving(true);
        try {
            const res = await api.put('/admin/mail-settings', payload);
            applyConfig(res.data);
            setNotice({ type: 'success', text: 'Сохранено. Новые письма уже уходят с этими настройками.' });
        } catch (err) {
            setNotice({ type: 'error', text: getApiErrorMessage(err, 'Не удалось сохранить настройки') });
        } finally {
            setSaving(false);
        }
    };

    const handleRemoveKey = async () => {
        if (!window.confirm('Удалить API-ключ, сохранённый в админке? Будет использоваться ключ из .env, если он задан.')) return;
        setSaving(true);
        try {
            const res = await api.put('/admin/mail-settings', { api_key: '' });
            applyConfig(res.data);
            setNotice({ type: 'success', text: 'Ключ удалён из админки.' });
        } catch (err) {
            setNotice({ type: 'error', text: getApiErrorMessage(err, 'Не удалось удалить ключ') });
        } finally {
            setSaving(false);
        }
    };

    const handleReset = async () => {
        if (!window.confirm('Сбросить все настройки почты, заданные в админке? Будут использоваться значения из .env.')) return;
        setSaving(true);
        try {
            const res = await api.delete('/admin/mail-settings');
            applyConfig(res.data);
            setNotice({ type: 'success', text: 'Настройки сброшены к значениям из .env.' });
        } catch (err) {
            setNotice({ type: 'error', text: getApiErrorMessage(err, 'Не удалось сбросить настройки') });
        } finally {
            setSaving(false);
        }
    };

    const handleTest = async () => {
        setTesting(true);
        setTestResult(null);
        try {
            const res = await api.post('/admin/mail-settings/test', {
                to: testTo.trim() || undefined,
                from_email: form.from_email.trim() || undefined,
                from_name: form.from_name.trim() || undefined,
                base_url: form.base_url.trim() || undefined,
                timeout_sec: Number(form.timeout_sec) || undefined,
                api_key: apiKey.trim() || undefined,
                target: testTarget,
                news_mode: newsMode,
                news_from_email: form.news_from_email.trim() || undefined,
                news_from_name: form.news_from_name.trim() || undefined,
                news_api_key: newsKey.trim() || undefined,
            });
            setTestResult(res.data);
        } catch (err) {
            setTestResult({ ok: false, error: getApiErrorMessage(err, 'Ошибка проверки') });
        } finally {
            setTesting(false);
        }
    };

    return (
        <section className="admin-main-card">
            <h2 className="admin-main-card__title">Почтовый сервер</h2>
            <div className="mai-body">
                <p className="mai-hint">
                    Через этот сервис уходят все письма СВОД: коды подтверждения, сброс пароля, приглашения,
                    отчёты и рассылки. Сейчас используется SMTP.BZ. Значения, сохранённые здесь, важнее файла .env
                    и начинают действовать сразу — перезапускать сервер не нужно.
                </p>

                <div className={`mai-status ${config.configured ? 'mai-status--ok' : 'mai-status--off'}`}>
                    <strong>Сейчас:</strong>{' '}
                    {config.configured
                        ? <>служебные письма уходят от «{config.from_name}» &lt;{config.from_email}&gt;</>
                        : 'почта не настроена — укажите API-ключ и адрес отправителя'}
                    <div>
                        рассылки — {config.news?.mode === 'same'
                            ? 'от того же адреса'
                            : <>от «{config.news.from_name}» &lt;{config.news.from_email}&gt;</>}
                        {config.news?.mode === 'key' ? ', через свой ключ' : ', через основной ключ'}
                    </div>
                </div>

                <section className="mail-section">
                <h3 className="mail-section__title"><span className="nl-step">1</span> Служебные письма</h3>
                <p className="mai-hint">
                    Коды подтверждения почты, сброс пароля, приглашения, отчёты по опросам, письма «вернитесь со скидкой».
                    Ключ из этого раздела — основной: рассылки тоже используют его, если в разделе 2 не задан свой ключ.
                </p>
                <div className="form-group">
                    <label htmlFor="mail-api-key">Основной API-ключ SMTP.BZ <SourceBadge source={config.api_key_set ? keySource : null} /></label>
                    <div className="mai-row">
                        <input
                            id="mail-api-key"
                            type="password"
                            autoComplete="new-password"
                            placeholder={config.api_key_set
                                ? `Сохранён: ${config.api_key_masked}. Оставьте пустым, чтобы не менять`
                                : 'Не задан'}
                            value={apiKey}
                            onChange={(e) => { setApiKey(e.target.value); setNotice(null); }}
                        />
                        {keySource === 'db' && (
                            <button type="button" className="btn btn--danger-outline btn--sm" onClick={handleRemoveKey} disabled={saving}>
                                Удалить
                            </button>
                        )}
                    </div>
                    <p className="mai-field-hint">Ключ берётся в личном кабинете SMTP.BZ → «Настройки» → API. Целиком ключ здесь больше не показывается.</p>
                </div>

                <div className="mai-grid mai-grid--even">
                    <div className="form-group">
                        <label htmlFor="mail-from">Адрес служебных писем <SourceBadge source={config.sources?.from_email} /></label>
                        <input id="mail-from" type="email" placeholder="no-reply@svodx.ru" value={form.from_email}
                               onChange={(e) => setField('from_email', e.target.value)} />
                    </div>
                    <div className="form-group">
                        <label htmlFor="mail-from-name">Имя <SourceBadge source={config.sources?.from_name} /></label>
                        <input id="mail-from-name" type="text" maxLength={80} placeholder="СВОД" value={form.from_name}
                               onChange={(e) => setField('from_name', e.target.value)} />
                    </div>
                </div>
                <p className="mai-field-hint mail-domain-hint">
                    Домен адреса отправителя должен быть подтверждён в SMTP.BZ (записи SPF и DKIM), иначе письма
                    будут отклонены или попадут в спам.
                </p>

                <details className="mail-advanced">
                    <summary>Дополнительно</summary>
                    <div className="mai-grid">
                        <div className="form-group">
                            <label htmlFor="mail-timeout">Тайм-аут, сек <SourceBadge source={config.sources?.timeout_sec} /></label>
                            <input id="mail-timeout" type="number" min="5" max="120" value={form.timeout_sec}
                                   onChange={(e) => setField('timeout_sec', e.target.value)} />
                        </div>
                        <div className="form-group">
                            <label htmlFor="mail-base-url">Адрес API <SourceBadge source={config.sources?.base_url} /></label>
                            <input id="mail-base-url" type="url" placeholder="https://api.smtp.bz/v1" value={form.base_url}
                                   onChange={(e) => setField('base_url', e.target.value)} />
                        </div>
                    </div>
                </details>
                </section>

                <section className="mail-section">
                    <h3 className="mail-section__title"><span className="nl-step">2</span> Рассылки</h3>
                    <p className="mai-hint">
                        Новости и скидки из вкладки «Рассылки». Если на рассылки жалуются или в них много неверных
                        адресов, почтовики начинают хуже доверять отправителю — отдельный адрес защищает коды подтверждения
                        и сброс пароля от попадания в спам.
                    </p>
                    <div className="mail-news__modes" role="radiogroup" aria-label="Отправитель рассылок">
                        {NEWS_MODES.map(([value, title, text]) => (
                            <label key={value} className={`mail-news__mode ${newsMode === value ? 'is-active' : ''}`}>
                                <input type="radio" name="news-mode" value={value} checked={newsMode === value}
                                       onChange={() => setField('news_mode', value)} />
                                <span><strong>{title}</strong><em>{text}</em></span>
                            </label>
                        ))}
                    </div>
                    {newsMode !== 'same' && (
                        <div className="mai-grid mai-grid--even">
                            <div className="form-group">
                                <label htmlFor="mail-news-from">Адрес для рассылок <SourceBadge source={config.sources?.news_from_email} /></label>
                                <input id="mail-news-from" type="email" placeholder="news@svodx.ru" value={form.news_from_email}
                                       onChange={(e) => setField('news_from_email', e.target.value)} />
                            </div>
                            <div className="form-group">
                                <label htmlFor="mail-news-name">Имя <SourceBadge source={config.sources?.news_from_name} /></label>
                                <input id="mail-news-name" type="text" maxLength={80} placeholder={`${form.from_name || 'СВОД'} — новости`}
                                       value={form.news_from_name} onChange={(e) => setField('news_from_name', e.target.value)} />
                            </div>
                        </div>
                    )}
                    {newsMode === 'key' && (
                        <div className="form-group">
                            <label htmlFor="mail-news-key">API-ключ SMTP.BZ для рассылок <SourceBadge source={config.news?.api_key_masked ? config.sources?.news_api_key : null} /></label>
                            <input id="mail-news-key" type="password" autoComplete="new-password" value={newsKey}
                                   placeholder={config.news?.api_key_masked
                                       ? `Сохранён: ${config.news.api_key_masked}. Оставьте пустым, чтобы не менять`
                                       : 'Ключ второго аккаунта SMTP.BZ'}
                                   onChange={(e) => { setNewsKey(e.target.value); setNotice(null); }} />
                        </div>
                    )}
                </section>

                {notice && <div className={`mai-alert mai-alert--${notice.type}`}>{notice.text}</div>}

                <div className="mai-actions">
                    <button type="button" className="btn btn--success" onClick={handleSave} disabled={saving || !isDirty}>
                        {saving ? 'Сохранение…' : 'Сохранить'}
                    </button>
                    <button type="button" className="btn btn--secondary" onClick={handleReset} disabled={saving}>
                        Сбросить к .env
                    </button>
                </div>

                <div className="mai-test">
                    <h3 className="mai-test__title">Проверочное письмо</h3>
                    <p className="mai-hint">
                        Отправляет письмо с настройками из формы — сохранять перед проверкой не обязательно.
                        Если адрес не указан, письмо придёт на вашу почту.
                    </p>
                    {newsMode !== 'same' && (
                        <div className="mail-test-target">
                            <span>Проверить:</span>
                            {[['main', 'служебные письма'], ['news', 'рассылки']].map(([value, label]) => (
                                <label key={value}>
                                    <input type="radio" name="test-target" value={value} checked={testTarget === value}
                                           onChange={() => setTestTarget(value)} /> {label}
                                </label>
                            ))}
                        </div>
                    )}
                    <div className="form-group mai-row">
                        <input type="email" placeholder="Ваша почта" value={testTo} onChange={(e) => setTestTo(e.target.value)}
                               aria-label="Куда отправить проверочное письмо" />
                        <button type="button" className="btn btn--primary" onClick={handleTest} disabled={testing}>
                            {testing ? 'Отправляем…' : 'Отправить проверку'}
                        </button>
                    </div>
                    {testResult && (
                        <div className={`mai-alert ${testResult.ok ? 'mai-alert--success' : 'mai-alert--error'} mai-test__result`} role="status">
                            {testResult.ok
                                ? <><strong>Письмо принято почтовым сервисом.</strong> Проверьте ящик {testResult.to} (и папку «Спам»).</>
                                : <><strong>Не отправлено.</strong><div className="mai-test__error">{testResult.error}</div></>}
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}

export default AdminMailSettings;
